import { useQuery } from "@tanstack/react-query"
import { Location, Setting } from "iconsax-reactjs"
import { useState } from "react"

import { getUserInfo } from "@/api"
import { Image, Skeleton } from "@/components/ui"
import { AvatarUploadModal } from "@/features/users/components/profile/avatar-upload-modal"
import { EFolderAssets } from "@/modules/helpers/enum"
import { useAuthStore, useNavigationLeft } from "@/modules/helpers/hooks/shared"
import { getImageByNameFromS3 } from "@/modules/helpers/utils"

// Constants
const AVATAR_SIZE = 128 // 32 * 4 (w-32 = 128px)
const QUERY_CONFIG = {
  gcTime: 80000,
  staleTime: 80000
}

// Types
interface ProfileHeaderProps {
  editable?: boolean
}

interface AvatarProps {
  avatarName?: string
  userId?: string | number
  isLoading: boolean
  onEdit: () => void
}

// Components

const Avatar: React.FC<AvatarProps> = ({ avatarName, userId, isLoading }) => {
  const getAvatarSrc = () => {
    return getImageByNameFromS3(avatarName, userId, EFolderAssets.AVATAR)
  }

  if (isLoading) {
    return <Skeleton className="w-full h-full object-cover rounded-full bg-gray-100" />
  }

  return (
    <Image
      className="w-20 h-20 rounded-full border border-gray-300 object-cover z-10"
      alt="Avatar"
      src={getAvatarSrc()}
      width={AVATAR_SIZE}
      height={AVATAR_SIZE}
      onError={(e) => {
        const target = e.target as HTMLImageElement
        target.src =
          "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQsBGOs2225fFqTfnl5EKlrEUBn5-drby1x3Q&s"
      }}
    />
  )
}

// Main Component
export const ProfileHeader: React.FC<ProfileHeaderProps> = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { setOpen } = useNavigationLeft()
  const { accessToken, notificationToken } = useAuthStore()

  const { data: user, isLoading } = useQuery({
    queryKey: ["user_info", notificationToken],
    queryFn: () => getUserInfo(notificationToken),
    ...QUERY_CONFIG,
    enabled: !!accessToken
  })

  const userData = user?.data?.data

  const handleEditAvatar = () => {
    setIsModalOpen(true)
  }
  const defaultAddress = userData?.contacts?.find((contact) => contact.isDefault)
  const address =
    [defaultAddress?.address, defaultAddress?.wards?.name, defaultAddress?.provinces?.name]
      .filter(Boolean)
      .join(", ") || ""

  return (
    <div className="mb-2 flex justify-start relative pr-4 py-2 gap-3 bg-white">
      <div className="flex w-20 h-20 rounded-full ml-3 relative">
        <Avatar
          avatarName={userData?.avatarName}
          userId={userData?.id}
          isLoading={isLoading}
          onEdit={handleEditAvatar}
        />
        <AvatarUploadModal open={isModalOpen} setIsVisible={setIsModalOpen} />
        {/* {editable && <EditButton onEdit={handleEditAvatar} />} */}
      </div>
      <div className="flex flex-col items-start gap-1 py-2 flex-1">
        <p className="text-sm font-bold text-primary-800">{userData?.username}</p>
        <p className="text-xs text-gray-500">{userData?.bio}</p>
        {address && (
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full flex items-center justify-center">
              <Location size={16} />
            </div>
            <span className="text-xs font-normal">{address}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 py-2 justify-between">
        {/* <p className="text-[10px] font-light">
          Ngày tham gia: {dayjs(userData?.createdAt).format("DD/MM/YYYY")}
        </p> */}
        <div
          onClick={() => setOpen(true)}
          className="text-[10px] bg-primary-50 font-semibold text-white px-2 py-2 rounded-full flex items-center gap-1 cursor-pointer"
        >
          <Setting size={16} className="text-primary-800" />
        </div>
      </div>
    </div>
  )
}
